import { Link } from "react-router-dom";
import { ArrowRight, Droplets, Users, Heart } from "lucide-react";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { ABOUT_HERO_IMAGE, ABOUT_STORY_IMAGE } from "@/data/site";

const STATS = [
  { value: "15+", label: "Years of Experience" },
  { value: "250+", label: "Projects Completed" },
  { value: "4", label: "Team Members" },
  { value: "200+", label: "Happy Clients" },
];

const VALUES = [
  { icon: Droplets, title: "Drainage Knowledge That Saves Homes", text: "Our deep drainage expertise means we spot problems others miss — and engineer solutions that actually last." },
  { icon: Users, title: "Professionalism, Always", text: "Clean sites, clear communication, and a respect for your property that's increasingly rare in this trade." },
  { icon: Heart, title: "Family Values That Fuel Results", text: "Three lifelong friends, one accountable team. We treat every project like it's for our own family." },
];

export default function About() {
  useSEO("About Us");
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-[#050505] pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal><p className="cc-eyebrow mb-6">About Us</p></Reveal>
            <Reveal delay={100}>
              <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-[60px] leading-[1.02] text-white">
                Built On Experience.<br />
                Focused On <span className="text-[#F28C00]">Quality</span>.
              </h1>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-[#9A9A9A] text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
                City Civils Construction Ltd was founded by three lifelong friends with over 30 years of combined experience in groundworks, drainage and landscaping. We grew up in Edinburgh, learned the trade across Central Scotland, and built this company on the simple belief that quality work and honest communication shouldn't be exceptions in construction.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="text-[#9A9A9A] text-base leading-relaxed mt-5 max-w-xl">
                Today we work with homeowners, developers, and commercial clients who expect their projects to be done right — the first time.
              </p>
            </Reveal>
            <Reveal delay={400}>
              <div className="mt-10">
                <Link to="/contact" className="cc-btn-primary" data-testid="about-cta-quote">
                  Get A Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal variant="img" className="relative">
            <div className="cc-hero-offset">
              <img src={ABOUT_HERO_IMAGE} alt="City Civils team on site" className="w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#F28C00] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="text-center md:text-left">
              <div className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-none">{s.value}</div>
              <div className="text-white/90 uppercase tracking-[0.15em] font-display font-bold text-xs mt-3">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-[#0B0B0B] py-24 md:py-32 border-b border-[#181818]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal variant="img" className="relative order-2 lg:order-1">
            <div className="cc-hero-offset">
              <img src={ABOUT_STORY_IMAGE} alt="Drainage and groundworks in progress" className="w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal><p className="cc-eyebrow mb-6">Our Story</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
                What Makes City Civils Construction Ltd Different
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-[#9A9A9A] text-base leading-relaxed mt-8">
                Most construction outfits chase volume. We chase craft. Every project is run by a partner — not a sub-contractor — and we only take on work we can deliver to our own standard. That's why our clients come back, and why their neighbours call us next.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="text-[#9A9A9A] text-base leading-relaxed mt-5">
                From storm-damaged fencing to full driveway installations and complex drainage works, we bring the same disciplined approach: scope it properly, build it cleanly, leave it tidy.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#050505] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal><p className="cc-eyebrow justify-center mb-6">Our Values</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
                The Standards We Build To
              </h2>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-6 cc-stagger">
            {VALUES.map((v) => (
              <Reveal key={v.title} className="group bg-[#111111] border border-[#181818] hover:border-[#F28C00]/60 p-8 transition-colors">
                <div className="w-12 h-12 inline-flex items-center justify-center bg-[#F28C00] text-white mb-6 group-hover:rotate-3 transition-transform">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold uppercase text-white text-xl tracking-tight mb-3 leading-snug">{v.title}</h3>
                <p className="text-[#9A9A9A] text-sm leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
